import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ArticlePendingItemDto, SetArticleActiveInput } from '@zoxo/shared';

import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';

/**
 * Admin approval queue (GET /articles/pending, V4 §C). Plain query (not Suspense)
 * — it's an admin-gated operational view that branches on session/role. Enabled
 * only when authenticated; the route additionally gates on role === 'admin'.
 * A non-admin caller gets a 403 from the server.
 */
export function usePendingArticles() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['articles', 'pending'],
    queryFn: () => api.get<ArticlePendingItemDto[]>('/articles/pending'),
    enabled: isAuthenticated,
  });
}

/**
 * Approve / hide an article (PATCH /articles/:id/active, admin-only). On success
 * the pending queue and the public list are invalidated so the change reflects.
 */
export function useSetArticleActive(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetArticleActiveInput) =>
      api.patch<void>(`/articles/${id}/active`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['articles', 'pending'] });
      void queryClient.invalidateQueries({ queryKey: ['articles'] });
      queryClient.removeQueries({ queryKey: ['article', id] });
    },
  });
}
